import style from "../style.module.scss";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { AdminType } from "../../../types/account";
import { callAsync } from "../../../util/sliceUtil";
import { useAppDispatch } from "../../../hooks/hooks";
import { findPwdAsync } from "../../../store/accountSlice";
import { useNavigate } from "react-router-dom";
import { phoneNumberInKRRegExpStr, phoneNumberInOtherRegExpStr } from "../../../util/regexpUtil";
import { useTranslation } from "react-i18next";
import { PrimaryOutlineButton } from "../../../components/Buttons";
import { PhoneCountryCodeUnderlineSelector } from "../../../components/PhoneCountryCodeUnderlineSelector";
import { useFindAccountHandleError } from "./useFindAccountHandleError";
import { useBlockEnter } from "../../../hooks/useBlockEnter";

export function FindPW() {
  const { t } = useTranslation(["account", "common"]);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const { blockEnterKeyEvent } = useBlockEnter();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [adminType] = useState(AdminType.GENERAL);
  const [phoneCountryCode, setPhoneCountryCode] = useState("");

  const isKoreanPhoneCountryCode = useMemo(() => phoneCountryCode === "+82", [phoneCountryCode]);
  const { handleError, errorMessage } = useFindAccountHandleError();
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const changeInputValue = useCallback((value: string, func: (_: string) => void) => {
    setShowErrorMessage(false);
    func(value);
  }, []);

  const onChangePhoneNumber = (phoneNumber: string) => {
    setPhoneNumber(phoneNumber.replace(/[^0-9]/g, ""));
  };
  const onClickFindPWBtn = async () => {
    if (formRef.current?.reportValidity()) {
      callAsync(
        dispatch(
          findPwdAsync({
            codePhone: phoneCountryCode,
            email,
            adminType,
            phoneNumber,
          })
        ).unwrap(),
        () => {
          navigate(
            `/find-account/pw-success?` +
              `email=${encodeURIComponent(email)}&phoneNumber=${phoneNumber}&adminType=${adminType}&` +
              `phoneCountryCode=${encodeURIComponent(phoneCountryCode)}`
          );
        },
        (error) => {
          setShowErrorMessage(true);
          handleError(error.errorCode.httpCode);
        }
      ).then();
    }
  };

  return (
    <form className={style.form} onSubmit={(e) => e.preventDefault()} ref={formRef}>
      <div className={style.field}>
        <label className={style.label}>{t("findAccount.findPW.id.label")}</label>
        <div className={style.line}>
          <input
            type={"email"}
            name={"email"}
            className={style.input}
            required={true}
            value={email}
            onChange={(e) => changeInputValue(e.target.value, setEmail)}
            placeholder={t("findAccount.findPW.id.placeholder")}
            onKeyDown={blockEnterKeyEvent}
          />
        </div>
        {showErrorMessage ? (
          <span className={`${style.addedMessage} ${style.notAvailableValue}`}>- {errorMessage}</span>
        ) : (
          <></>
        )}
      </div>
      <div className={style.field}>
        <label className={style.label}>{t("findAccount.findPW.phoneNumber.label")}</label>
        <div className={style.line}>
          <PhoneCountryCodeUnderlineSelector
            code={phoneCountryCode}
            setCode={(code) => changeInputValue(code, setPhoneCountryCode)}
          />
        </div>
        <div className={style.line} style={{ marginTop: "0" }}>
          <input
            type={"tel"}
            name={"phone"}
            className={style.input}
            required={true}
            value={phoneNumber}
            onChange={(e) => changeInputValue(e.target.value, onChangePhoneNumber)}
            maxLength={isKoreanPhoneCountryCode ? 11 : 15}
            pattern={isKoreanPhoneCountryCode ? phoneNumberInKRRegExpStr : phoneNumberInOtherRegExpStr}
            placeholder={
              isKoreanPhoneCountryCode
                ? t("inputPhoneNumber.placeholder1", { ns: "common" })
                : t("inputPhoneNumber.placeholder2", { ns: "common" })
            }
            onKeyDown={blockEnterKeyEvent}
          />
        </div>
        <span className={`${style.addedMessage} ${style.normalValue}`}>- {t("findAccount.phoneNumber.support")}</span>
      </div>
      <div className={style.buttonWrapper}>
        <PrimaryOutlineButton className={style.cancelButton} onClick={() => navigate(`/login`)}>
          {t("findAccount.cancelButton")}
        </PrimaryOutlineButton>
        <button className={style.findButton} onClick={onClickFindPWBtn}>
          {t("findAccount.findPW.buttonLabel")}
        </button>
      </div>
    </form>
  );
}
